import { Close, ContentCopy, WarningAmber } from "@mui/icons-material";
import {
  Badge,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { fontStyles } from "components/Common/commonStyles";
import TextLabel from "components/Common/TextLabel/TextLabel";
import { CustomButton } from "components/RosterConfig/RosterConfigStyle";
import BackdropComponent from "components/Spinner/Spinner";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { getInformaticaEmployeeData, getOracleIdsPII } from "services/userServices";
import { SnackBarStoreAction } from "Store/SnackBar";
import { useAuth } from "context/AuthContext";
import piiAuditLog from "utils/piiAuditLog";

export default function ShippingReportByRITM(props) {
  const dispatch = useDispatch();
  const { hasPermission, hasAnyPermission } = useAuth();
  const { ticketNumber } = props;
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [omittedDialogOpen, setOmittedDialogOpen] = useState(false);
  const [omittedRecords, setOmittedRecords] = useState([]);

  const isOmittedFeatureEnabled = hasAnyPermission([
    "PII_Group_FULL",
    "PII_Group_Med",
  ]);

  let fieldsToFetch = [
    "contact_number",
    "country",
    "street_address_1",
    "street_address_2",
    "city",
    "state",
    "postal_code",
    "work_email",
    "personal_email",
  ];

  const showSnack = (message, severity) => {
    const obj = {
      open: true,
      message: message,
      severity: severity,
    };
    dispatch(SnackBarStoreAction.updateSnack(obj));
  };

  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "",
  });

  const snackReset = () => {
    setSnack({
      open: false,
      message: "",
      severity: "",
    });
  };

  const computeOmittedRecords = (piiData, informaticaData) => {
    if (!isOmittedFeatureEnabled) return;

    const informaticaOracleIds = new Set(
      (informaticaData?.employee_datalist ?? []).map((e) => e.oracle_id),
    );

    const seenOracleIds = new Set();
    const omitted = [];

    (piiData ?? []).forEach((row) => {
      const oracleId = row?.OracleId;
      if (!oracleId || seenOracleIds.has(oracleId)) return;
      seenOracleIds.add(oracleId);

      if (!informaticaOracleIds.has(oracleId)) {
        omitted.push({
          classRosterId: row.ClassRosterId,
          oracleId: row.OracleId,
          firstName: row.FirstName,
          lastName: row.LastName,
          employeeStatus: row.EmployeeStatus,
          traineeStatus: row.TraineeStatus,
          createdBy: row.CreatedBy,
        });
      }
    });

    setOmittedRecords(omitted);
  };

  const fetchEmployeeData = async (oracleIds) => {
    const payload = {
      oracle_id: oracleIds,
      show_pii_field: fieldsToFetch.join(","),
    };
    const prvAccess = false;
    setLoading(true);
    const employeeDataResponse = await getInformaticaEmployeeData(
      payload,
      prvAccess,
    );
    setLoading(false);
    if (employeeDataResponse.status === true) {
      setReportData(employeeDataResponse.data);

      // ASTT-7304: PII Access Logging. Only log when PII was actually shown.
      if (employeeDataResponse.data?.employee_datalist?.length > 0) {
        piiAuditLog({
          oracleIds,
          userGroup: hasPermission("PII_Group_FULL")
            ? "PII_Group_FULL"
            : "PII_Group_Med",
          sourceModule: "ShippingReportByRITM",
          moduleReference: ticketNumber,
          piiFieldsShown: fieldsToFetch.join(","),
          piiFieldsMasked: "",
        });
      }
    }
    return employeeDataResponse;
  };

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      if (!cancelled) {
        setLoading(true);
      }

      try {
        const oracleIdsResponse = await getOracleIdsPII(ticketNumber);

        if (cancelled) return;

        if (oracleIdsResponse?.data?.length === 0) {
          if (!cancelled) {
            showSnack(
              "No Trainees data found for the ticket number",
              "warning",
            );
          }
        }

        if (
          oracleIdsResponse?.status === true &&
          oracleIdsResponse?.data[0]?.OracleIds !== null
        ) {
          const employeeDataResponse = await fetchEmployeeData(
            oracleIdsResponse?.data[0]?.OracleIds,
          );

          if (!cancelled) {
            computeOmittedRecords(
              oracleIdsResponse?.data,
              employeeDataResponse?.data,
            );
          }
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to fetch PII data:", error);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleCopyToClipBoard = () => {
    const header = [
      "Contact",
      "Company",
      "Address1",
      "Address2",
      "City",
      "State",
      "Zip",
      "CountryCode",
      "Phone",
      "Email",
      "Oracle",
    ].join("\t");

    const rows = (reportData?.employee_datalist ?? []).map((row) => {
      const jobInformation = row?.job_information;
      const personalInformation = row?.personal_information;
      return [
        `${jobInformation?.first_name} ${jobInformation?.last_name}`,
        row?.oracle_id,
        personalInformation?.street_address_1,
        personalInformation?.street_address_2,
        personalInformation?.city,
        personalInformation?.state,
        personalInformation?.postal_code,
        personalInformation?.country,
        personalInformation?.contact_number,
        personalInformation?.personal_email,
        row?.oracle_id,
      ].join("\t");
    });

    navigator.clipboard.writeText([header, ...rows].join("\n"));
    showSnack("Copied to Clipboard!", "success");
  };

  const handleOpenOmittedDialog = () => setOmittedDialogOpen(true);

  const handleCloseOmittedDialog = () => setOmittedDialogOpen(false);

  const handleCopyOmittedToClipboard = () => {
    const header = [
      "Class Roster ID",
      "Oracle ID",
      "First Name",
      "Last Name",
      "Employee Status",
      "Trainee Status",
      "Created By",
    ].join("\t");
    const rows = omittedRecords.map((r) =>
      [
        r.classRosterId,
        r.oracleId,
        r.firstName,
        r.lastName,
        r.employeeStatus,
        r.traineeStatus,
        r.createdBy,
      ].join("\t"),
    );
    navigator.clipboard.writeText([header, ...rows].join("\n"));
    showSnack("Omitted records copied to clipboard!", "success");
  };

  const contentFontStyle = {
    ...fontStyles,
    fontSize: 14,
    letterSpacing: 0.1,
  };

  return (
    <>
      {loading ? <BackdropComponent /> : null}
      <Box sx={fontStyles}>
        <Paper>
          <Grid container>
            <Grid item xs={12}>
              <Grid container alignItems="center" wrap="nowrap">
                {isOmittedFeatureEnabled && (
                  <Grid item>
                    <CustomButton
                      variant="outlined"
                      onClick={handleOpenOmittedDialog}
                      title="View omitted records"
                      disabled={omittedRecords.length === 0}
                      endIcon={
                        <Badge
                          badgeContent={omittedRecords.length}
                          color="warning"
                          invisible={omittedRecords.length === 0}
                        >
                          <WarningAmber
                            color={
                              omittedRecords.length > 0
                                ? "warning"
                                : "disabled"
                            }
                            fontSize="small"
                          />
                        </Badge>
                      }
                      sx={{
                        borderColor:
                          omittedRecords.length > 0
                            ? "warning.light"
                            : "grey.300",
                        borderRadius: 0,
                        height: "100%",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Omitted Records
                    </CustomButton>
                  </Grid>
                )}
                <Grid item xs>
                  <CustomButton
                    fullWidth
                    variant="contained"
                    sx={{
                      backgroundColor: "#3299FF",
                      color: "#FFF",
                      borderRadius: 0,
                    }}
                    endIcon={<ContentCopy />}
                    onClick={handleCopyToClipBoard}
                  >
                    Copy to Clipboard
                  </CustomButton>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <TableContainer
                sx={{ height: "calc(100vh - 35px)", overflowY: "auto" }}
              >
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <TextLabel text="Contact" />
                      </TableCell>
                      <TableCell>
                        <TextLabel text="Company" />
                      </TableCell>
                      <TableCell>
                        <TextLabel text="Address1" />
                      </TableCell>
                      <TableCell>
                        <TextLabel text="Address2" />
                      </TableCell>
                      <TableCell>
                        <TextLabel text="City" />
                      </TableCell>
                      <TableCell>
                        <TextLabel text="State" />
                      </TableCell>
                      <TableCell>
                        <TextLabel text="Zip" />
                      </TableCell>
                      <TableCell>
                        <TextLabel text="CountryCode" />
                      </TableCell>
                      <TableCell>
                        <TextLabel text="Phone" />
                      </TableCell>
                      <TableCell>
                        <TextLabel text="Email" />
                      </TableCell>
                      <TableCell>
                        <TextLabel text="Oracle" />
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reportData?.employee_datalist?.map((row, index) => {
                      const key = `${row.contact_number}-${index}`;
                      const jobInformation = row?.job_information;
                      const personalInformation = row?.personal_information;
                      return (
                        <TableRow key={key}>
                          <TableCell sx={contentFontStyle}>
                            {`${jobInformation?.first_name} ${jobInformation?.last_name}`}
                          </TableCell>
                          <TableCell sx={contentFontStyle}>
                            {row?.oracle_id}
                          </TableCell>
                          <TableCell sx={contentFontStyle}>
                            {personalInformation?.street_address_1}
                          </TableCell>
                          <TableCell sx={contentFontStyle}>
                            {personalInformation?.street_address_2}
                          </TableCell>
                          <TableCell sx={contentFontStyle}>
                            {personalInformation?.city}
                          </TableCell>
                          <TableCell sx={contentFontStyle}>
                            {personalInformation?.state}
                          </TableCell>
                          <TableCell sx={contentFontStyle}>
                            {personalInformation?.postal_code}
                          </TableCell>
                          <TableCell sx={contentFontStyle}>
                            {personalInformation?.country}
                          </TableCell>
                          <TableCell sx={contentFontStyle}>
                            {personalInformation?.contact_number}
                          </TableCell>
                          <TableCell sx={contentFontStyle}>
                            {personalInformation?.personal_email}
                          </TableCell>
                          <TableCell sx={contentFontStyle}>
                            {row?.oracle_id}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        </Paper>
      </Box>

      {isOmittedFeatureEnabled && (
        <Dialog
          open={omittedDialogOpen}
          onClose={handleCloseOmittedDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span>
              <WarningAmber
                color="warning"
                sx={{ verticalAlign: "middle", mr: 1 }}
              />
              Omitted Records
            </span>
            <IconButton onClick={handleCloseOmittedDialog} size="small">
              <Close fontSize="small" />
            </IconButton>
          </DialogTitle>

          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              These trainees were excluded from the shipping roster.
            </Typography>

            {omittedRecords.length === 0 && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ py: 3, textAlign: "center" }}
              >
                No records were omitted for this ticket.
              </Typography>
            )}

            {omittedRecords.length > 0 && (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Roster ID</TableCell>
                    <TableCell>Oracle ID</TableCell>
                    <TableCell>First Name</TableCell>
                    <TableCell>Last Name</TableCell>
                    <TableCell>Employee Status</TableCell>
                    <TableCell>Trainee Status</TableCell>
                    <TableCell>Created By</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {omittedRecords.map((r) => (
                    <TableRow key={`${r.classRosterId}-${r.oracleId}`}>
                      <TableCell>{r.classRosterId}</TableCell>
                      <TableCell>{r.oracleId}</TableCell>
                      <TableCell>{r.firstName}</TableCell>
                      <TableCell>{r.lastName}</TableCell>
                      <TableCell>{r.employeeStatus}</TableCell>
                      <TableCell>{r.traineeStatus}</TableCell>
                      <TableCell>{r.createdBy}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            {omittedRecords.length > 0 && (
              <CustomButton
                variant="contained"
                sx={{
                  backgroundColor: "#3299FF",
                  color: "#FFF",
                }}
                onClick={handleCopyOmittedToClipboard}
              >
                Copy to Clipboard
              </CustomButton>
            )}
            <CustomButton
              variant="outlined"
              sx={{ borderColor: "#3299FF", color: "#3299FF" }}
              onClick={handleCloseOmittedDialog}
            >
              Close
            </CustomButton>
          </DialogActions>
        </Dialog>
      )}

      <Snackbar snack={snack} snackReset={snackReset}></Snackbar>
    </>
  );
}

ShippingReportByRITM.propTypes = {
  ticketNumber: PropTypes.string.isRequired,
};
